import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import * as Icons from "@nova/component-icons"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectActiveWorkspaceId,
    selectWorkspaceRegion,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import {
    clearSelection,
    removeLayerFromSelection,
    selectSelectedLayerIds,
    selectSelectedLayers,
} from "../slice.new"
import {
    concurrentArchiveCount,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
    Licenses,
} from "@nova/constants"
import store from "integrations/redux/store"
import { selectEntityById } from "../slice"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ButtonAlternate, ButtonTertiary } from "@nova/component-btn"
import { deleteGroup } from "features/pubsub/entityActions/cubes/groups"
import { getNestedLayerCount } from "./genericMultipleSelectedContextMenuActions"
import { selectIsDomainWhiteListed, selectUserID, selectUserLicense } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { useMemo } from "react"
import { Semaphore } from "async-mutex"
import { getSidePanelLayerTypeToPluginNameMap } from "plugins/plugin-registry"

const useCanArchive = () => {
    const isEditor = useIsEditor()
    const license = useAppSelector(selectUserLicense)
    const isWhitelisted = useAppSelector(selectIsDomainWhiteListed)
    return isEditor && (license?.subscriptionType === Licenses.Pro || isWhitelisted)
}

export const useArchiveContextMenuOption = (id: string): ContextMenuItem | undefined => {
    const dispatch = useAppDispatch()
    const entity = useAppSelector(selectEntityById(id))
    const confirmationModal = useConfirmationModalContext()
    const orgId = useAppSelector(selectActiveWorkspaceId()) || ""
    const projectId = useAppSelector(selectActivatedFireId) || ""
    const userId = useAppSelector(selectUserID) || ""
    const region = useAppSelector(selectWorkspaceRegion()) || ""
    const canArchive = useCanArchive()
    if (!canArchive) return
    return {
        title: "Archive",
        onClick: async () => {
            await confirmationModal.confirm({
                RenderComponent: getArchiveConfirmationModal({
                    layerCount: 1 + getNestedLayerCount(entity.children || []),
                }),
                asyncConfirmCallback: async () => {
                    await recursivelyAndConcurrentlyArchiveEntities({
                        entities: [entity],
                        orgId,
                        projectId,
                        userId,
                        region,
                    })
                },
            })
            dispatch(removeLayerFromSelection({ id }))
        },
        Icon: Icons.Archive,
    }
}

export const useArchiveMultipleSelectedLayersContextMenuOption = ():
    | ContextMenuItem
    | undefined => {
    const dispatch = useAppDispatch()
    const confirmationModal = useConfirmationModalContext()
    const orgId = useAppSelector(selectActiveWorkspaceId()) || ""
    const projectId = useAppSelector(selectActivatedFireId) || ""
    const userId = useAppSelector(selectUserID) || ""
    const region = useAppSelector(selectWorkspaceRegion()) || ""
    const canArchive = useCanArchive()
    const archiveAction = useMemo(() => {
        if (!canArchive) return
        return {
            title: "Archive",
            onClick: async () => {
                const state = store.getState()
                const selectedLayers = selectSelectedLayers(state)
                const selectedLayerIds = selectSelectedLayerIds(state)
                await confirmationModal.confirm({
                    RenderComponent: getArchiveConfirmationModal({
                        layerCount: getNestedLayerCount(selectedLayers),
                    }),
                    asyncConfirmCallback: async () => {
                        await recursivelyAndConcurrentlyArchiveEntities({
                            entities: selectedLayerIds.map((id) => selectEntityById(id)(state)),
                            orgId,
                            projectId,
                            userId,
                            region,
                        })
                    },
                })
                dispatch(clearSelection())
            },
            Icon: Icons.Archive,
        }
    }, [canArchive, confirmationModal, dispatch, orgId, projectId, region, userId])
    return archiveAction
}

function getArchiveConfirmationModal({ layerCount }: { layerCount: number }) {
    const ArchiveConfirmationModal = ({
        confirm,
        cancel,
        isPerformingAsyncConfirmCallback,
        isPerformingAsyncCancelCallback,
    }: {
        confirm: () => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
        isPerformingAsyncCancelCallback: boolean
    }) => {
        return (
            <HighPriorityNovaModalContainer
                title={layerCount > 1 ? "Archive Selection?" : "Archive Layer?"}
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <ButtonTertiary
                            title="Cancel"
                            onClick={cancel}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncCancelCallback}
                        />
                        <ButtonAlternate
                            title={
                                layerCount > 1 ? `Archive ${layerCount} Layers` : "Archive Layer"
                            }
                            onClick={confirm}
                            LeftIcon={Icons.Archive}
                            disabled={
                                isPerformingAsyncConfirmCallback || isPerformingAsyncCancelCallback
                            }
                            isLoading={isPerformingAsyncConfirmCallback}
                        />
                    </div>
                }
                closeModal={cancel}
                className="!min-w-[410px]"
            >
                <div className="mt-4 flex flex-col gap-4">
                    <h4>
                        Archive {layerCount > 1 ? `${layerCount} layers` : "layer"}, do you want to
                        proceed?
                    </h4>
                    <h4 className="text-gray-300">Archived layers can be retrieved later.</h4>
                </div>
            </HighPriorityNovaModalContainer>
        )
    }
    return ArchiveConfirmationModal
}

const multipleArchiveSemaphore = new Semaphore(concurrentArchiveCount)

async function recursivelyAndConcurrentlyArchiveEntities({
    entities,
    orgId,
    projectId,
    userId,
    region,
}: {
    entities: SidePanelLayerOrGroup[]
    orgId: string
    projectId: string
    userId: string
    region: string
}) {
    const entitiesToArchive = getEntitiesToArchive({ entities })
    let errorCount = 0
    await Promise.all(
        entitiesToArchive.map((entity) => {
            const task = async () => {
                const [, release] = await multipleArchiveSemaphore.acquire()
                try {
                    const res = await fetch(
                        `/api/protobuf/org/${orgId}/project/${projectId}/user/${userId}/${getSidePanelLayerTypeToPluginNameMap()[entity.type]}/${entity.id}/archive`,
                        {
                            method: "POST",
                            headers: {
                                region,
                            },
                        }
                    )
                    if (!res.ok) {
                        errorCount += 1
                    }
                } catch {
                    errorCount += 1
                } finally {
                    release()
                }
            }
            return task()
        })
    )
    if (errorCount > 0) {
        store.dispatch(
            setGlobalNotificationContext({
                message: `Failed to archive ${errorCount} ${errorCount > 1 ? "layers" : "layer"}`,
                status: GlobalNotificationStatus.error,
                timeoutInMs: 3000,
            })
        )
    }
}

type EntityWithDefinedType = SidePanelLayerOrGroup & { type: SidePanelLayerType }
function getEntitiesToArchive({ entities }: { entities: SidePanelLayerOrGroup[] }) {
    const entitiesToArchive = [] as EntityWithDefinedType[]
    for (const entity of entities) {
        if (!entity?.type) continue
        if (entity.children) {
            entitiesToArchive.push(...getEntitiesToArchive({ entities: entity.children }))
        }
        if (entity.type === SidePanelLayerType.Group) {
            store.dispatch(deleteGroup({ id: entity.id }))
        } else if (entity.type !== SidePanelLayerType.PDF) {
            entitiesToArchive.push(entity as EntityWithDefinedType)
        }
    }
    return entitiesToArchive
}
